/**
 * Контролы смены статуса операции (AK-525).
 *
 * До этого во всём вебе не было НИ ОДНОГО контрола смены статуса операции —
 * статус можно было только читать. Кнопки строятся по
 * `allowed_lifecycle_states` из API (граф переходов — на бэке, AK-524),
 * поэтому предложить нелегальный переход интерфейс не может в принципе.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { statusActions, type StatusAction } from './opLifecycle';

interface Props {
  lifecycleState: string;
  allowedStates?: string[] | null;
  /** Идёт запрос — кнопки блокируются, чтобы не отправить два перехода подряд. */
  busy?: boolean;
  /** Нет права operations:U — контролы не рендерятся вовсе (fail-closed). */
  canWrite: boolean;
  onTransition: (target: string) => void;
}

export function OperationStatusActions({
  lifecycleState,
  allowedStates,
  busy,
  canWrite,
  onTransition,
}: Props) {
  const [pendingAction, setPendingAction] = useState<StatusAction | null>(null);

  if (!canWrite) return null;
  const actions = statusActions(lifecycleState, allowedStates);
  if (actions.length === 0) return null;

  const run = (a: StatusAction) => {
    if (a.confirm) setPendingAction(a);
    else onTransition(a.target);
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {actions.map((a) => (
          <Button
            key={a.target}
            variant={a.variant}
            size="sm"
            disabled={busy}
            onClick={() => run(a)}
          >
            {a.label}
          </Button>
        ))}
      </div>

      <Dialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pendingAction?.label}</DialogTitle>
            <DialogDescription>{pendingAction?.confirm}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAction(null)}>
              Не сейчас
            </Button>
            <Button
              onClick={() => {
                if (pendingAction) onTransition(pendingAction.target);
                setPendingAction(null);
              }}
            >
              {pendingAction?.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default OperationStatusActions;
